import {
  ArrayPath,
  Control,
  FieldArray,
  FieldValues,
  useFieldArray,
} from 'react-hook-form';
import { useCallback } from 'react';
import { SupportedLocale } from '@/types/supported-locale.type';

interface UseTranslationFieldArrayProps<FieldsType extends FieldValues> {
  control: Control<FieldsType>;
  name: ArrayPath<FieldsType>;
}

export const useTranslationFieldArray = <FieldsType extends FieldValues>({
  control,
  name,
}: UseTranslationFieldArrayProps<FieldsType>) => {
  const {
    append: appendTranslationFrench,
    remove: removeTranslationFrench,
    insert: insertTranslationFrench,
  } = useFieldArray({
    control,
    name: `translations.fr.${name}` as ArrayPath<FieldsType>,
  });
  const {
    append: appendTranslationEnglish,
    remove: removeTranslationEnglish,
    insert: insertTranslationEnglish,
  } = useFieldArray({
    control,
    name: `translations.en.${name}` as ArrayPath<FieldsType>,
  });
  const {
    append: appendTranslationGerman,
    remove: removeTranslationGerman,
    insert: insertTranslationGerman,
  } = useFieldArray({
    control,
    name: `translations.de.${name}` as ArrayPath<FieldsType>,
  });
  const {
    append: appendTranslationItalian,
    remove: removeTranslationItalian,
    insert: insertTranslationItalian,
  } = useFieldArray({
    control,
    name: `translations.it.${name}` as ArrayPath<FieldsType>,
  });
  const {
    append: appendTranslationSpanish,
    remove: removeTranslationSpanish,
    insert: insertTranslationSpanish,
  } = useFieldArray({
    control,
    name: `translations.es.${name}` as ArrayPath<FieldsType>,
  });

  const append = useCallback(
    (
      getLanguage: (
        lang: SupportedLocale,
      ) => FieldArray<FieldsType, ArrayPath<FieldsType>>,
    ) => {
      appendTranslationFrench(
        getLanguage('fr') as FieldArray<FieldsType, ArrayPath<FieldsType>>,
      );
      appendTranslationEnglish(
        getLanguage('en') as FieldArray<FieldsType, ArrayPath<FieldsType>>,
      );
      appendTranslationGerman(
        getLanguage('de') as FieldArray<FieldsType, ArrayPath<FieldsType>>,
      );
      appendTranslationItalian(
        getLanguage('it') as FieldArray<FieldsType, ArrayPath<FieldsType>>,
      );
      appendTranslationSpanish(
        getLanguage('es') as FieldArray<FieldsType, ArrayPath<FieldsType>>,
      );
    },
    [
      appendTranslationEnglish,
      appendTranslationFrench,
      appendTranslationGerman,
      appendTranslationItalian,
      appendTranslationSpanish,
    ],
  );

  const insert = useCallback(
    (
      index: number,
      getLanguage: (
        lang: SupportedLocale,
      ) => FieldArray<FieldsType, ArrayPath<FieldsType>>,
    ) => {
      insertTranslationFrench(
        index,
        getLanguage('fr') as FieldArray<FieldsType, ArrayPath<FieldsType>>,
      );
      insertTranslationEnglish(
        index,
        getLanguage('en') as FieldArray<FieldsType, ArrayPath<FieldsType>>,
      );
      insertTranslationGerman(
        index,
        getLanguage('de') as FieldArray<FieldsType, ArrayPath<FieldsType>>,
      );
      insertTranslationItalian(
        index,
        getLanguage('it') as FieldArray<FieldsType, ArrayPath<FieldsType>>,
      );
      insertTranslationSpanish(
        index,
        getLanguage('es') as FieldArray<FieldsType, ArrayPath<FieldsType>>,
      );
    },
    [
      insertTranslationEnglish,
      insertTranslationFrench,
      insertTranslationGerman,
      insertTranslationItalian,
      insertTranslationSpanish,
    ],
  );

  const remove = useCallback(
    (index: number) => {
      removeTranslationFrench(index);
      removeTranslationEnglish(index);
      removeTranslationGerman(index);
      removeTranslationItalian(index);
      removeTranslationSpanish(index);
    },
    [
      removeTranslationEnglish,
      removeTranslationFrench,
      removeTranslationGerman,
      removeTranslationItalian,
      removeTranslationSpanish,
    ],
  );
  return { append, remove, insert };
};
